// @flow
import ExperimentsClient from 'app/common/lib/ExperimentsClient';
import SeoExperimentsClient from 'app/common/lib/ExperimentsClient/SeoExperimentsClient';
import SeoUnauthExperimentsClient from 'app/common/lib/ExperimentsClient/SeoUnauthExperimentsClient';
import SideEffects from 'app/common/lib/SideEffects';

type ExperimentGroupMap = {|
  [expName: string]: string,
|};
type ExperimentBuilderContext = {|
  active_experiments: ExperimentGroupMap,
  triggerable_experiments: ExperimentGroupMap,
  seo_experiments: ExperimentGroupMap,
  is_bot: 'true' | 'false',
  path: string,
  batch_exp?: boolean,
|};

export type ExperimentsClientsMap = {|
  experiments: ExperimentsClient,
  seoExperiments: ExperimentsClient,
  seoUnauthExperiments: ExperimentsClient,
|};

export default function buildExperimentsClients(
  legacy_server_context: ExperimentBuilderContext | Object,
  sideEffects?: SideEffects,
): ExperimentsClientsMap {
  if (!legacy_server_context) {
    legacy_server_context = {};
  }
  const {
    active_experiments: activeExperiments,
    triggerable_experiments: triggerableExperiments,
    seo_experiments: seoExperiments,
    is_bot,
    path,
    batch_exp: useBatching,
  } = legacy_server_context;
  const isBot = is_bot === 'true';

  const experimentsClient = new ExperimentsClient(
    activeExperiments,
    triggerableExperiments,
    useBatching,
  );

  const seoExperimentsClient = new SeoExperimentsClient(seoExperiments, {}, useBatching);

  // Build seoUnauthExperiment client. If user is Bot, only supply SEO experiments. Otherwise, supply
  // all active and triggerable experiments for unauth. Client will handle triggering of either unauth or
  // SEO experiment
  const seoUnauthExperimentsClient = new SeoUnauthExperimentsClient(
    isBot ? seoExperiments : activeExperiments,
    isBot ? {} : triggerableExperiments,
    isBot,
    useBatching,
  );

  // If this component has been passed a sideEffects prop, that means that we are being
  // server-side template rendered and will need to queue any activations so that Python/Node.js
  // can enact these side effects.
  if (sideEffects) {
    const queuedV2ActivateExperiment: string[] = sideEffects.queuedV2ActivateExperiment;
    const queuedV2ExperimentGroupOverrides: Object[] = sideEffects.queuedV2ExperimentGroupOverrides;
    const queuedLogTrfExperiments: Object[] = sideEffects.queuedLogTrfExperiments;

    (experimentsClient: Object).v2ActivateExperiment = function v2ActivateExperiment(name) {
      queuedV2ActivateExperiment.push(name);
      return this.v2GetGroup(name);
    };

    // For seo/unauth experiments created in Pinalytics, you must use 'seo_' prefix to trigger seo experiment
    // because config automatically appends 'seo_' to seo experiment. This client will not work for unauth.
    // For seo experiments created in seo_experiments.py, there is no need to append 'seo_' prefix.
    // NOTE: There is no bot check here, so you must manually do is_bot before activating seo experiment.
    (seoExperimentsClient: Object).v2ActivateExperiment = function v2ActivateSeoExperiment(
      name,
    ): ?string {
      queuedLogTrfExperiments.push({
        name,
        id: path,
      });
      return this.v2GetGroup(name);
    };

    // Return an SEO experiment group or activate unauth experiment and return unauth group
    (seoUnauthExperimentsClient: Object).v2ActivateExperiment = function v2ActivateSeoOrUnauth(
      name,
    ) {
      // Check if unauth experiment, otherwise check to see if this is an SEO experiment
      if (this.v2GetGroup(name)) {
        queuedV2ActivateExperiment.push(name);
        return this.v2GetGroup(name);
      } else {
        const seoGrp = this.v2GetSEOGroup('seo_' + name);
        if (seoGrp) {
          queuedLogTrfExperiments.push({
            name: 'seo_' + name,
            id: path,
          });
          return seoGrp;
        } else {
          return undefined;
        }
      }
    };

    // Log an override group for an experiment
    (seoUnauthExperimentsClient: Object).v2LogExperimentGroupOverride = function v2LogOverrideGroup(
      name,
      overrideGroup,
    ) {
      queuedV2ExperimentGroupOverrides.push({ name, override_group: overrideGroup });
    };
  }

  return {
    experiments: experimentsClient,
    seoExperiments: seoExperimentsClient,
    seoUnauthExperiments: seoUnauthExperimentsClient,
  };
}
